import * as vp from 'vopay';
import { useId, useRef } from 'react';
import { motion } from 'motion/react';

import styles from './TabButton.module.scss';

const { withStyles, getContrastColor, getCSSVariable } = vp;
const cn = withStyles(styles);

type TabOption<TValue extends string> = {
  label: string;
  value: TValue;
};

type TabButtonProps<TValue extends string> = {
  options: TabOption<TValue>[];
  onSelect: (value: TValue) => void;
  value: TValue;
  disabled: boolean;
};

const TabButton = <TValue extends string>({ options, onSelect, disabled, value }: TabButtonProps<TValue>) => {
  const containerRef = useRef<HTMLDivElement>(null);
  // useId (not crypto.randomUUID) so it also works in non-secure contexts (http:// on a LAN IP)
  const id = useId();

  const getCurrentContrastColor = () => {
    const primaryColor = getCSSVariable('--primaryColor');

    return getContrastColor(primaryColor);
  };

  return (
    <div className={cn('tabButtonContainer', disabled && 'disabled')} ref={containerRef}>
      {options.map((opt) => (
        <button
          disabled={disabled}
          key={opt.value}
          className={cn('tabButtonOption')}
          onClick={() => onSelect(opt.value)}
          style={value === opt.value ? { color: getCurrentContrastColor(), fontWeight: 400 } : undefined}
        >
          {value === opt.value && (
            <motion.div
              layoutId={`tabBg-${id}`}
              className={cn('tabButtonHighlight')}
              style={{ backgroundColor: 'var(--primaryColor)' }}
              transition={{
                type: 'spring',
                mass: 0.2,
                stiffness: 200,
                damping: 20,
              }}
            />
          )}

          {opt.label}
        </button>
      ))}
    </div>
  );
};

export default TabButton;
